import { toast } from 'react-toastify'
import { updateMessage } from './utils'
import { FC, useState, useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import {
  setPatientInfo,
  selectPatientInfo,
  setAddDeviceCount,
} from './ProrithmSlice'
import {
  searchPatient,
  editProrithmMapping,
} from '../../services/ProrithmServices'
import {
  Box,
  Button,
  Dialog,
  Divider,
  Typography,
  DialogTitle,
  DialogContent,
} from '@mui/material'
import {
  messages,
  constants,
  mappingStatus,
  IpdSearchOptions,
  ProrithmSocketMessage,
  SmartRingRemapPayload,
} from './Constants'
import SearchByPatient from './SearchByPatient'

interface RemapSmartRingProps {
  open: boolean
  deviceId: string
  mappingId: string | null
  categoryType: string | null
  isEditMode?: boolean
  onClose: () => void
  sendMessageOnSocket: (payload: ProrithmSocketMessage) => void
}

const RemapSmartRing: FC<RemapSmartRingProps> = ({
  open,
  deviceId,
  mappingId,
  categoryType,
  isEditMode = false,
  onClose,
  sendMessageOnSocket,
}) => {
  const dispatch = useAppDispatch()
  const patientInfo = useAppSelector(selectPatientInfo)
  const [searchQue, setSearchQue] = useState<{
    key: string
    value: string
  } | null>(null)
  const [ipdSearchData, setIpdSearchData] = useState<IpdSearchOptions[] | null>(
    null
  )

  const fetchPatientsList = useCallback(async () => {
    const type = searchQue?.key ?? ''
    const value = searchQue?.value ?? ''
    const data = await searchPatient(type, value, false)
    if (data?.success) {
      setIpdSearchData(data?.data ?? null)
      dispatch(setPatientInfo(null))
    } else {
      setIpdSearchData([])
    }
  }, [dispatch, searchQue?.key, searchQue?.value])

  const handleSearch = (query: string, param: string) => {
    const trimmedQuery = query?.trim()
    if (!trimmedQuery) {
      setSearchQue(null)
      return
    }
    setSearchQue({ key: param, value: trimmedQuery })
  }

  const handleSmartRingMapping = async () => {
    if (!patientInfo?.muhId) {
      toast(messages?.patientIdNotFound, {
        position: 'bottom-center',
        hideProgressBar: true,
        closeButton: false,
        theme: 'dark',
        autoClose: 4000,
      })
      return
    }

    const payload: SmartRingRemapPayload = {
      patientId: patientInfo?.muhId,
      deviceId: deviceId,
      deviceType: constants?.RING,
    }

    const response = await editProrithmMapping(mappingId, payload)
    if (response?.success) {
      dispatch(setAddDeviceCount())
      toast(response?.message || messages?.smartRingMappedSuccessfully, {
        position: 'bottom-center',
        hideProgressBar: true,
        closeButton: false,
        theme: 'dark',
        autoClose: 4000,
      })

      const updateMappingMessage: ProrithmSocketMessage = {
        messageType: constants?.update,
        messageContent: {
          message: updateMessage(categoryType, deviceId),
          macId: deviceId,
          category: categoryType || '',
        },
      }
      sendMessageOnSocket(updateMappingMessage)
      handleClose()
    }
  }

  const handleClose = () => {
    dispatch(setPatientInfo(null))
    setSearchQue(null)
    setIpdSearchData(null)
    onClose()
  }

  useEffect(() => {
    if (searchQue) fetchPatientsList()
  }, [fetchPatientsList, searchQue])

  useEffect(() => {
    if (!open) {
      setSearchQue(null)
      setIpdSearchData(null)
      dispatch(setPatientInfo(null))
    }
  }, [open, dispatch])

  return (
    <Dialog fullWidth maxWidth='md' open={open}>
      <DialogTitle>{constants?.remapSmartRing}</DialogTitle>
      <Divider />
      <DialogContent>
        {!isEditMode && mappingId ? (
          <>
            <Typography id='smart-ring-already-mapped'>
              {mappingStatus?.ringStatus}
            </Typography>
            <Box display='flex' justifySelf='flex-end' maxWidth={200}>
              <Button onClick={handleClose}>{constants?.close}</Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant='body1'>{constants?.selectPatient}:</Typography>
            <SearchByPatient
              mapTo=''
              handleSearch={handleSearch}
              data={ipdSearchData}
            />
            <Box
              marginY={2}
              maxWidth={200}
              display='flex'
              justifySelf='flex-end'
            >
              <Button onClick={handleClose}>{constants?.close}</Button>
              <Button onClick={handleSmartRingMapping} disabled={!patientInfo}>
                {constants?.submit}
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default RemapSmartRing
